/**
 * Consultants Management Page
 * إدارة المستشارين - للخدمات من نوع "استشارات"
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  UserCheck, Plus, Edit2, Trash2, ToggleLeft, ToggleRight, X, Check,
  AlertTriangle, Info, Search, Star, MapPin, Clock, Phone, Video,
  Building, Globe, Filter, RefreshCw, ChevronDown, Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/catalog';

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════

const COLORS = {
  primary: '#1D8F4E',
  primaryLight: '#E8F5EE',
  secondary: '#1B3A6B',
  secondaryLight: '#EEF2F7',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  success: '#059669',
  warning: '#D97706',
  error: '#DC2626',
  info: '#2563EB',
  purple: '#8B5CF6',
};

const DELIVERY_MODES = [
  { value: 'in_person', label: 'حضوري', icon: Building },
  { value: 'online', label: 'أونلاين', icon: Video },
  { value: 'hybrid', label: 'هجين', icon: Globe },
];

const EXPERTISE_AREAS = [
  'ريادة الأعمال',
  'التسويق',
  'المالية',
  'الموارد البشرية',
  'التخطيط الاستراتيجي',
  'التقنية',
  'العمليات',
  'الجودة',
  'القانون',
  'أخرى',
];

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

function getAuthHeaders() {
  const token = localStorage.getItem('hala_admin_token');
  return { Authorization: `Bearer ${token}` };
}

// ═══════════════════════════════════════════════════════════════════════════════
// UI COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

function Alert({ type = 'info', children, style = {} }) {
  const configs = {
    info: { bg: '#EFF6FF', border: '#3B82F6', color: '#1D4ED8', Icon: Info },
    warning: { bg: '#FFFBEB', border: '#F59E0B', color: '#B45309', Icon: AlertTriangle },
    error: { bg: '#FEF2F2', border: '#EF4444', color: '#B91C1C', Icon: AlertTriangle },
    success: { bg: '#F0FDF4', border: '#22C55E', color: '#15803D', Icon: Check },
  };
  const config = configs[type];
  return (
    <div style={{
      padding: '14px 18px', borderRadius: '10px', backgroundColor: config.bg,
      borderRight: `4px solid ${config.border}`, display: 'flex', alignItems: 'flex-start',
      gap: '12px', ...style
    }}>
      <config.Icon size={18} color={config.color} style={{ flexShrink: 0, marginTop: '2px' }} />
      <div style={{ fontSize: '14px', color: config.color, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function Button({ children, variant = 'primary', size = 'md', icon: Icon, loading, disabled, onClick, style = {} }) {
  const variants = {
    primary: { bg: COLORS.primary, color: '#fff' },
    secondary: { bg: COLORS.secondary, color: '#fff' },
    outline: { bg: 'transparent', color: COLORS.text, border: COLORS.border },
    ghost: { bg: 'transparent', color: COLORS.textMuted },
    danger: { bg: COLORS.error, color: '#fff' },
    success: { bg: COLORS.success, color: '#fff' },
  };
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px', gap: '6px' },
    md: { padding: '10px 18px', fontSize: '14px', gap: '8px' },
    lg: { padding: '12px 24px', fontSize: '15px', gap: '10px' },
  };
  const v = variants[variant];
  const s = sizes[size];
  
  return (
    <button onClick={onClick} disabled={disabled || loading} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      gap: s.gap, padding: s.padding, fontSize: s.fontSize, fontWeight: 600,
      fontFamily: "'Tajawal', sans-serif", borderRadius: '10px',
      border: v.border ? `1.5px solid ${v.border}` : 'none',
      backgroundColor: v.bg, color: v.color, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1, transition: 'all 0.2s', ...style
    }}>
      {loading ? <RefreshCw size={16} className="animate-spin" /> : Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

function Input({ value, onChange, placeholder, type = 'text', style = {} }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={{
      width: '100%', padding: '12px 14px', borderRadius: '10px',
      border: `1.5px solid ${COLORS.border}`, backgroundColor: COLORS.white,
      fontSize: '14px', fontFamily: "'Tajawal', sans-serif", color: COLORS.text,
      outline: 'none', ...style
    }} />
  );
}

function Select({ value, onChange, options, placeholder, style = {} }) {
  return (
    <select value={value} onChange={onChange} style={{
      width: '100%', padding: '12px 14px', borderRadius: '10px',
      border: `1.5px solid ${COLORS.border}`, backgroundColor: COLORS.white,
      fontSize: '14px', fontFamily: "'Tajawal', sans-serif", color: COLORS.text,
      cursor: 'pointer', outline: 'none', ...style
    }}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
  );
}

function FormField({ label, required, hint, error, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'block', marginBottom: '8px', fontSize: '14px',
        fontWeight: 600, color: COLORS.text,
      }}>
        {label}
        {required && <span style={{ color: COLORS.error, marginRight: '4px' }}>*</span>}
        {hint && <span style={{ fontWeight: 400, color: COLORS.textMuted, marginRight: '8px', fontSize: '12px' }}>({hint})</span>}
      </label>
      {children}
      {error && <p style={{ margin: '6px 0 0', fontSize: '12px', color: COLORS.error }}>{error}</p>}
    </div>
  );
}

function StarRating({ value, onChange, readOnly = false }) {
  return (
    <div style={{ display: 'flex', gap: '4px' }}>
      {[1, 2, 3, 4, 5].map(star => (
        <Star
          key={star}
          size={24}
          fill={star <= value ? '#F59E0B' : 'transparent'}
          color={star <= value ? '#F59E0B' : COLORS.border}
          style={{ cursor: readOnly ? 'default' : 'pointer' }}
          onClick={() => !readOnly && onChange(star)}
        />
      ))}
    </div>
  );
}

function MultiSelect({ options, selected, onChange, placeholder }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const toggleOption = (val) => {
    if (selected.includes(val)) {
      onChange(selected.filter(s => s !== val));
    } else {
      onChange([...selected, val]);
    }
  };
  
  return (
    <div style={{ position: 'relative' }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          minHeight: '48px', padding: '8px 12px', borderRadius: '10px',
          border: `1.5px solid ${COLORS.border}`, backgroundColor: COLORS.white,
          cursor: 'pointer', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center',
        }}
      >
        {selected.length === 0 ? (
          <span style={{ fontSize: '14px', color: COLORS.textMuted }}>{placeholder}</span>
        ) : (
          selected.map(s => (
            <span key={s} style={{
              padding: '4px 10px', backgroundColor: `${COLORS.purple}15`,
              borderRadius: '6px', fontSize: '12px', color: COLORS.purple, fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: '4px',
            }}>
              {s}
              <X size={12} onClick={(e) => { e.stopPropagation(); toggleOption(s); }} style={{ cursor: 'pointer' }} />
            </span>
          ))
        )}
        <ChevronDown size={16} color={COLORS.textMuted} style={{ marginRight: 'auto' }} />
      </div>
      
      {isOpen && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 50 }} onClick={() => setIsOpen(false)} />
          <div style={{
            position: 'absolute', top: '100%', right: 0, left: 0, marginTop: '4px',
            backgroundColor: COLORS.white, borderRadius: '10px', border: `1px solid ${COLORS.border}`,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)', zIndex: 51, maxHeight: '250px', overflowY: 'auto',
          }}>
            {options.map(opt => (
              <div
                key={opt}
                onClick={() => toggleOption(opt)}
                style={{
                  padding: '10px 14px', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'space-between',
                  backgroundColor: selected.includes(opt) ? `${COLORS.purple}10` : 'transparent',
                  borderBottom: `1px solid ${COLORS.border}`,
                }}
              >
                <span style={{ fontSize: '14px' }}>{opt}</span>
                {selected.includes(opt) && <Check size={16} color={COLORS.purple} />}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONSULTANT CARD
// ═══════════════════════════════════════════════════════════════════════════════

function ConsultantCard({ consultant, onEdit, onToggle, onDelete }) {
  const modeInfo = DELIVERY_MODES.find(m => m.value === consultant.delivery_mode);
  const ModeIcon = modeInfo?.icon || Globe;
  
  return (
    <div style={{
      padding: '20px', borderRadius: '16px', backgroundColor: COLORS.white,
      border: `1px solid ${COLORS.border}`, opacity: consultant.is_active ? 1 : 0.6,
      transition: 'all 0.2s',
    }}>
      <div style={{ display: 'flex', gap: '16px' }}>
        {/* Avatar */}
        <div style={{
          width: '64px', height: '64px', borderRadius: '16px',
          backgroundColor: `${COLORS.purple}15`, display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          fontSize: '24px', fontWeight: 700, color: COLORS.purple,
        }}>
          {consultant.name_ar?.charAt(0) || 'م'}
        </div>
        
        {/* Info */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600, color: COLORS.text }}>
              {consultant.name_ar}
            </h3>
            {!consultant.is_active && (
              <span style={{
                padding: '2px 8px', backgroundColor: '#FEE2E2', borderRadius: '4px',
                fontSize: '11px', color: COLORS.error, fontWeight: 500,
              }}>معطل</span>
            )}
          </div>
          
          {consultant.short_description && (
            <p style={{ margin: '0 0 10px', fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.5 }}>
              {consultant.short_description}
            </p>
          )}
          
          {/* Meta */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Clock size={14} color={COLORS.textMuted} />
              <span style={{ fontSize: '13px', color: COLORS.text }}>{consultant.experience_years || 0} سنوات خبرة</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <StarRating value={consultant.rating || 0} readOnly />
              <span style={{ fontSize: '13px', color: COLORS.textMuted }}>({consultant.rating || 0})</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <ModeIcon size={14} color={COLORS.textMuted} />
              <span style={{ fontSize: '13px', color: COLORS.text }}>{modeInfo?.label || 'غير محدد'}</span>
            </div>
          </div>
          
          {/* Expertise Areas */}
          {consultant.expertise_areas?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
              {consultant.expertise_areas.map(area => (
                <span key={area} style={{
                  padding: '4px 10px', backgroundColor: COLORS.bg, borderRadius: '6px',
                  fontSize: '12px', color: COLORS.text,
                }}>{area}</span>
              ))}
            </div>
          )}
          
          {/* Cities */}
          {consultant.cities?.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <MapPin size={14} color={COLORS.textMuted} />
              <span style={{ fontSize: '13px', color: COLORS.textMuted }}>
                {consultant.cities.join('، ')}
              </span>
            </div>
          )}
          
          {/* Service Link */}
          {consultant.service_name_ar && (
            <div style={{ marginTop: '10px', padding: '8px 12px', backgroundColor: COLORS.bg, borderRadius: '8px' }}>
              <span style={{ fontSize: '12px', color: COLORS.textMuted }}>الخدمة: </span>
              <span style={{ fontSize: '13px', fontWeight: 500, color: COLORS.text }}>{consultant.service_name_ar}</span>
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <button onClick={() => onToggle(consultant)} title={consultant.is_active ? 'تعطيل' : 'تفعيل'}
            style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: 'transparent',
              color: consultant.is_active ? COLORS.success : COLORS.textMuted, cursor: 'pointer' }}>
            {consultant.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
          </button>
          <button onClick={() => onEdit(consultant)} title="تعديل"
            style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: COLORS.secondaryLight,
              color: COLORS.secondary, cursor: 'pointer' }}>
            <Edit2 size={16} />
          </button>
          <button onClick={() => onDelete(consultant)} title="حذف"
            style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: '#FEE2E2',
              color: COLORS.error, cursor: 'pointer' }}>
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ADD/EDIT MODAL
// ═══════════════════════════════════════════════════════════════════════════════

function ConsultantModal({ isOpen, onClose, onSave, services, cities, editingItem }) {
  const [formData, setFormData] = useState({
    service_id: '', name_ar: '', name_en: '', short_description: '', details: '',
    experience_years: '', rating: 5, delivery_mode: 'hybrid',
    expertise_areas: [], cities: [], branches: '', booking_link: '', contact_info: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filter services - Only "استشارات" type
  const consultingServices = services.filter(s => 
    s.category_type === 'consulting' || s.category_name_ar?.includes('استشار')
  );
  
  useEffect(() => {
    if (isOpen) {
      if (editingItem) {
        setFormData({
          service_id: editingItem.service_id || '',
          name_ar: editingItem.name_ar || '',
          name_en: editingItem.name_en || '',
          short_description: editingItem.short_description || '',
          details: editingItem.details || '',
          experience_years: editingItem.experience_years || '',
          rating: editingItem.rating || 5,
          delivery_mode: editingItem.delivery_mode || 'hybrid',
          expertise_areas: editingItem.expertise_areas || [],
          cities: editingItem.cities || [],
          branches: editingItem.branches || '',
          booking_link: editingItem.booking_link || '',
          contact_info: editingItem.contact_info || '',
        });
      } else {
        setFormData({
          service_id: '', name_ar: '', name_en: '', short_description: '', details: '',
          experience_years: '', rating: 5, delivery_mode: 'hybrid',
          expertise_areas: [], cities: [], branches: '', booking_link: '', contact_info: '',
        });
      }
      setError('');
    }
  }, [isOpen, editingItem]);
  
  const validate = () => {
    if (!formData.service_id) return 'يجب اختيار الخدمة';
    if (!formData.name_ar.trim()) return 'اسم المستشار بالعربية مطلوب';
    if (!formData.short_description.trim()) return 'النبذة المختصرة مطلوبة';
    if (!formData.experience_years) return 'سنوات الخبرة مطلوبة';
    if (!formData.rating) return 'التقييم مطلوب';
    return null;
  };
  
  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await onSave({
        ...formData,
        resource_type: 'consultant',
        experience_years: parseInt(formData.experience_years) || 0,
        rating: parseInt(formData.rating) || 5,
      });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'حدث خطأ أثناء الحفظ');
    }
    setLoading(false);
  };
  
  if (!isOpen) return null;
  
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }} />
      
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '750px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto',
        backgroundColor: COLORS.white, borderRadius: '16px', boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, backgroundColor: COLORS.white, zIndex: 1,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: COLORS.text }}>
              {editingItem ? 'تعديل بيانات المستشار' : 'إضافة مستشار جديد'}
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
              المستشارون مرتبطون بخدمات الاستشارات فقط
            </p>
          </div>
          <button onClick={onClose} style={{
            width: '36px', height: '36px', borderRadius: '8px', border: 'none',
            backgroundColor: COLORS.bg, cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={20} color={COLORS.textMuted} />
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '24px' }}>
          {error && <Alert type="error" style={{ marginBottom: '20px' }}>{error}</Alert>}
          
          {consultingServices.length === 0 && (
            <Alert type="warning" style={{ marginBottom: '20px' }}>
              لا توجد خدمات استشارية في الكتالوج. يرجى إضافة خدمة من نوع "استشارات" أولاً.
            </Alert>
          )}
          
          <FormField label="الخدمة" required hint="خدمات الاستشارات فقط">
            <Select
              value={formData.service_id}
              onChange={e => setFormData({ ...formData, service_id: e.target.value })}
              placeholder="-- اختر الخدمة --"
              options={consultingServices.map(s => ({ value: s.service_id, label: s.name_ar }))}
            />
          </FormField>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="الاسم بالعربية" required>
              <Input value={formData.name_ar} onChange={e => setFormData({ ...formData, name_ar: e.target.value })}
                placeholder="مثال: م. أحمد العمري" />
            </FormField>
            <FormField label="الاسم بالإنجليزية">
              <Input value={formData.name_en} onChange={e => setFormData({ ...formData, name_en: e.target.value })}
                placeholder="Example: Eng. Ahmed Al-Omari" />
            </FormField>
          </div>
          
          <FormField label="نبذة مختصرة" required hint="تظهر للمستخدم">
            <Input value={formData.short_description} onChange={e => setFormData({ ...formData, short_description: e.target.value })}
              placeholder="مستشار متخصص في ريادة الأعمال وتطوير المشاريع الناشئة" />
          </FormField>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="سنوات الخبرة" required>
              <Input type="number" value={formData.experience_years}
                onChange={e => setFormData({ ...formData, experience_years: e.target.value })}
                placeholder="10" />
            </FormField>
            <FormField label="التقييم" required>
              <StarRating value={formData.rating} onChange={val => setFormData({ ...formData, rating: val })} />
            </FormField>
          </div>
          
          <FormField label="نمط التقديم" required>
            <div style={{ display: 'flex', gap: '12px' }}>
              {DELIVERY_MODES.map(mode => {
                const Icon = mode.icon;
                const isSelected = formData.delivery_mode === mode.value;
                return (
                  <button key={mode.value} type="button"
                    onClick={() => setFormData({ ...formData, delivery_mode: mode.value })}
                    style={{
                      flex: 1, padding: '12px', borderRadius: '10px', border: `2px solid ${isSelected ? COLORS.purple : COLORS.border}`,
                      backgroundColor: isSelected ? `${COLORS.purple}10` : COLORS.white, cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                    }}>
                    <Icon size={20} color={isSelected ? COLORS.purple : COLORS.textMuted} />
                    <span style={{ fontSize: '13px', fontWeight: isSelected ? 600 : 400, color: isSelected ? COLORS.purple : COLORS.text }}>
                      {mode.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </FormField>
          
          <FormField label="المجالات" hint="اختر مجالات الخبرة">
            <MultiSelect
              options={EXPERTISE_AREAS}
              selected={formData.expertise_areas}
              onChange={vals => setFormData({ ...formData, expertise_areas: vals })}
              placeholder="-- اختر المجالات --"
            />
          </FormField>
          
          {(formData.delivery_mode === 'in_person' || formData.delivery_mode === 'hybrid') && (
            <FormField label="المدن" hint="للتقديم الحضوري">
              <MultiSelect
                options={cities.map(c => c.name_ar)}
                selected={formData.cities}
                onChange={vals => setFormData({ ...formData, cities: vals })}
                placeholder="-- اختر المدن --"
              />
            </FormField>
          )}
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="رابط الحجز">
              <Input value={formData.booking_link} onChange={e => setFormData({ ...formData, booking_link: e.target.value })}
                placeholder="https://calendly.com/..." />
            </FormField>
            <FormField label="وسيلة التواصل">
              <Input value={formData.contact_info} onChange={e => setFormData({ ...formData, contact_info: e.target.value })}
                placeholder="البريد أو رقم الهاتف" />
            </FormField>
          </div>
        </div>
        
        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: `1px solid ${COLORS.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: '12px', backgroundColor: COLORS.bg,
          position: 'sticky', bottom: 0,
        }}>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button variant="success" icon={Check} onClick={handleSubmit} loading={loading}>
            {editingItem ? 'حفظ التعديلات' : 'إضافة المستشار'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export default function ConsultantsPage() {
  const [consultants, setConsultants] = useState([]);
  const [services, setServices] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterService, setFilterService] = useState('');
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [resRes, svcRes, citiesRes] = await Promise.all([
        axios.get(`${API}/resources`, { params: { resource_type: 'consultant' }, headers: getAuthHeaders() }),
        axios.get(`${API}/services`, { headers: getAuthHeaders() }),
        axios.get(`${API}/cities`, { headers: getAuthHeaders() }),
      ]);
      
      setConsultants(resRes.data || []);
      const svcsData = svcRes.data.items || svcRes.data;
      setServices(Array.isArray(svcsData) ? svcsData : []);
      setCities(citiesRes.data || []);
    } catch (e) {
      console.error('Failed to fetch:', e);
    }
    setLoading(false);
  }, []);
  
  useEffect(() => { fetchData(); }, [fetchData]);
  
  const handleSave = async (data) => {
    if (editingItem) {
      await axios.put(`${API}/resources/${editingItem.resource_id}`, data, { headers: getAuthHeaders() });
    } else {
      await axios.post(`${API}/resources`, data, { headers: getAuthHeaders() });
    }
    fetchData();
    setEditingItem(null);
  };
  
  const handleToggle = async (item) => {
    await axios.post(`${API}/resources/${item.resource_id}/toggle`, {}, { headers: getAuthHeaders() });
    fetchData();
  };
  
  const handleDelete = async (item) => {
    if (!window.confirm(`هل أنت متأكد من حذف "${item.name_ar}"؟`)) return;
    await axios.delete(`${API}/resources/${item.resource_id}`, { headers: getAuthHeaders() });
    fetchData();
  };
  
  const filtered = consultants.filter(c => {
    if (filterService && c.service_id !== filterService) return false;
    if (searchQuery && !c.name_ar?.includes(searchQuery)) return false;
    return true;
  });
  
  const activeCount = consultants.filter(c => c.is_active).length;
  
  return (
    <div style={{ padding: '24px', backgroundColor: COLORS.bg, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '12px',
            backgroundColor: `${COLORS.purple}15`, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <UserCheck size={24} color={COLORS.purple} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 700, color: COLORS.text }}>المستشارون</h1>
            <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
              إدارة المستشارين المرتبطين بخدمات الاستشارات
            </p>
          </div>
        </div>
      </div>
      
      {/* Info Box */}
      <Alert type="info" style={{ marginBottom: '24px' }}>
        <strong>ملاحظة:</strong> المستشارون يُعرضون فقط للخدمات من نوع "استشارات". تأكد من وجود خدمات استشارية في الكتالوج قبل إضافة مستشارين.
      </Alert>
      
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'إجمالي المستشارين', value: consultants.length, color: COLORS.purple },
          { label: 'مستشارون نشطون', value: activeCount, color: COLORS.success },
          { label: 'مستشارون معطلون', value: consultants.length - activeCount, color: COLORS.warning },
        ].map((stat, i) => (
          <div key={i} style={{
            padding: '20px', borderRadius: '12px', backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.border}`,
          }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: '14px', color: COLORS.textMuted }}>{stat.label}</div>
          </div>
        ))}
      </div>
      
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <Search size={18} color={COLORS.textMuted} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="بحث بالاسم..."
            style={{ paddingRight: '40px' }} />
        </div>
        <Select value={filterService} onChange={e => setFilterService(e.target.value)}
          placeholder="كل الخدمات"
          options={services.filter(s => s.category_type === 'consulting' || s.category_name_ar?.includes('استشار'))
            .map(s => ({ value: s.service_id, label: s.name_ar }))}
          style={{ width: '220px' }} />
        <Button variant="primary" icon={Plus} onClick={() => { setEditingItem(null); setShowModal(true); }}>
          إضافة مستشار
        </Button>
      </div>
      
      {/* List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: COLORS.textMuted }}>
            <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
            <p>جاري التحميل...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '60px', backgroundColor: COLORS.white,
            borderRadius: '16px', border: `1px solid ${COLORS.border}`,
          }}>
            <UserCheck size={48} color={COLORS.textMuted} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: COLORS.text }}>
              لا يوجد مستشارون
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: COLORS.textMuted }}>
              {searchQuery || filterService ? 'لا توجد نتائج تطابق البحث' : 'قم بإضافة مستشارين لخدمات الاستشارات'}
            </p>
            <Button variant="primary" icon={Plus} onClick={() => { setEditingItem(null); setShowModal(true); }}>
              إضافة أول مستشار
            </Button>
          </div>
        ) : (
          filtered.map(c => (
            <ConsultantCard key={c.resource_id} consultant={c}
              onEdit={(item) => { setEditingItem(item); setShowModal(true); }}
              onToggle={handleToggle} onDelete={handleDelete} />
          ))
        )}
      </div>
      
      {/* Modal */}
      <ConsultantModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingItem(null); }}
        onSave={handleSave}
        services={services}
        cities={cities}
        editingItem={editingItem}
      />
    </div>
  );
}
